import React, { useMemo, useState } from "react";
import {
  ListFilterConfig,
  ListFilterConfigItemMultiSelectOption,
  ListFilterConfigItemType,
} from "../../components/list-filter/models";
import useInternshipCities from "../../app/hooks/useInternshipCities";
import useInternshipCompanies from "../../app/hooks/useInternshipCompanies";
import { useTranslation } from "react-i18next";
import useListFilter from "../../components/list-filter/useListFilter";
import useInternshipIndustries from "../../app/hooks/useInternshipIndustries";

export interface InternshipFilter {
  cities: ListFilterConfigItemMultiSelectOption[];
  remote: ListFilterConfigItemMultiSelectOption[];
  companies: ListFilterConfigItemMultiSelectOption[];
  industries: ListFilterConfigItemMultiSelectOption[];
  search: string;
  date: { start: Date; end: Date } | null;
  onlyAvailableSlots?: boolean;
}

const useInternshipFilter = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState<InternshipFilter>({
    cities: [],
    search: "",
    remote: [],
    companies: [],
    industries: [],
    date: null,
    onlyAvailableSlots: undefined,
  });

  const { cities } = useInternshipCities(value.date);
  const { companyOptions } = useInternshipCompanies(value.date);
  const { industryOptions } = useInternshipIndustries(value.date);

  const config: ListFilterConfig = useMemo(
    () => [
      {
        name: "search",
        title: "internship.filter.search",
        type: ListFilterConfigItemType.Search,
      },
      {
        name: "cities",
        title: "internship.filter.cities",
        type: ListFilterConfigItemType.MultiSelect,
        options: cities.map((city: string) => ({ key: city, value: city, text: city })),
      },
      {
        name: "onlyAvailableSlots",
        title: "shadowings_filter.occupancy_group",
        type: ListFilterConfigItemType.Checkbox,
        label: "shadowings_filter.only_available_slots",
      },
      {
        name: "remote",
        title: "internship.filter.remote",
        type: ListFilterConfigItemType.MultiSelect,
        options: [
          {
            key: "remote",
            value: "true",
            text: t("internship.filter.only_remote"),
          },
        ],
      },
      {
        name: "companies",
        title: "internship.filter.companies",
        type: ListFilterConfigItemType.MultiSelect,
        options: companyOptions,
      },
      {
        name: "industries",
        title: "internship.filter.industries",
        type: ListFilterConfigItemType.MultiSelect,
        options: industryOptions,
      },
      {
        name: "date",
        title: "internship.filter.date",
        type: ListFilterConfigItemType.DateRange,
      },
    ],
    [cities, companyOptions, industryOptions, t]
  );

  const { renderDeleteBadges, renderSearch, renderFilterAccordion } = useListFilter({
    config,
    value,
    onChange: setValue as any,
  });

  return { value, renderSearch, renderDeleteBadges, renderFilterAccordion };
};

export default useInternshipFilter;
